import {
  Box,
  DismissIcon,
  Flex,
  Image,
  JaneGoldIcon,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';

import { getCartItemWeight } from '../../../utils/productCard/getCartItemWeight';
import { productPhotoMetadata } from '../../../utils/productPhotoMetadata';
import styles from '../specialDetailStatus.module.css';
import type { SpecialProduct } from '../specialDetailStatus.types';

/** Props for a single product row within the bundle progress tracker */
export interface ProductRowProps {
  /** Whether this product receives the discount */
  isDiscounted: boolean;
  /** Whether this is a Jane Gold special */
  isGold: boolean;
  /** Whether the special qualification has been unlocked */
  isUnlocked: boolean;
  /** Callback fired when the remove button is clicked */
  onRemoveProduct: (...args: any[]) => void;
  /** Product data including photo metadata and name */
  product: SpecialProduct;
  /** Discount tag label (e.g., "$5 back") */
  tagLabel: string;
}
export const ProductRow = ({
  isDiscounted,
  isGold,
  isUnlocked,
  onRemoveProduct,
  product,
  tagLabel,
}: ProductRowProps) => {
  const showTag = isDiscounted && tagLabel;

  const { url: photoUrl } = productPhotoMetadata(product);

  const showWeight = product.price_id !== 'each';
  const weightString = showWeight ? ` (${getCartItemWeight(product)})` : '';

  return (
    <Flex alignItems="center" data-testid="product-row">
      <Box position="relative">
        <Image
          src={photoUrl}
          height="48px"
          width="48px"
          altText={product.name}
          borderRadius="8px"
          className={styles['border__light__gray']}
        />
        <Flex
          top={-8}
          right={-8}
          width={24}
          height={24}
          position="absolute"
          background="grays-white"
          border="grays-light"
          borderRadius="lg"
          alignItems="center"
          justifyContent="center"
        >
          <DismissIcon
            onClick={() => onRemoveProduct(product)}
            altText={`Remove ${product.name}`}
            size="sm"
            className={styles['icon__xs']}
          />
        </Flex>
      </Box>

      <Typography ml={16} truncateAt="100%">
        {product.name}
        {weightString}
      </Typography>

      {showTag && (
        <Tag
          ml="auto"
          label={tagLabel}
          icon={isGold ? <JaneGoldIcon /> : undefined}
          color={isGold ? 'grays-black' : 'grays-white'}
          background={isGold ? 'gold' : 'info'}
          className={clsx(styles['white__space__no__wrap'], {
            [styles['opacity__not__unlocked']]: !isUnlocked,
          })}
          data-testid="product-tag"
        />
      )}
    </Flex>
  );
};
